import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { CaretLeftIcon, CaretRightIcon, CaretDownIcon } from '@phosphor-icons/react';
import {

    Box,
    Typography,
    IconButton,
    CircularProgress,
    Fade,
    Zoom,
    Slide,
    useTheme,
    Dialog,
    useMediaQuery,
    Snackbar,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { keyframes } from '@mui/system';
import { ArrowBack, ArrowForward, ArrowUpward, Close, Check, PlaylistAdd, Undo, Star, StarBorder, StarHalf, VisibilityOff, Language, PlayArrow, Replay, KeyboardArrowUp } from '@mui/icons-material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';


import { Chip, Stack, Button } from '@mui/material';
import MarkAsWatchedDialog from '../dialogs/MarkAsWatchedDialog';
import { tmdbService as tmdbApi, TMDbMovie } from '../../services/tmdbApi';
import { OnboardingData } from './OnboardingFlow';
import { getStandardizedMovieId } from '../../utils/movieUtils';
import CustomButton from '../widgets/CustomButton';
import ActionLoader from '../widgets/ActionLoader';
import { hapticLight, hapticSuccess, hapticWarning } from '../../utils/nativeHaptics';
import { useOrientationChange } from '../../hooks/useOrientationChange';
import { getOptimalCardDimensions } from '../../utils/orientationUtils';
import { font } from '../../utils/fontHelper';

interface MovieTasteStepV2Props {
    movies: TMDbMovie[];
    preferences: OnboardingData['moviePreferences'];
    selectedLanguages: string[];
    onPreferencesUpdate: (preferences: OnboardingData['moviePreferences']) => void;
    onComplete: () => void;
    onBack?: () => void;
    loading?: boolean;
    excludeMovieIds?: Set<string>;
    onMoviesSeen?: (movieIds: string[]) => void;
}

interface ActionHistoryItem {
    movieId: string;
    action: 'discard' | 'watched' | 'want';
    previousRating?: number;
}

// ----------------------------------------------------------------------
// Constants & Styles
// ----------------------------------------------------------------------
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SWIPE_THRESHOLD = 100;
const MAX_UNDOS = 3;

const ALL_AVAILABLE_LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'hi', name: 'Hindi' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'ta', name: 'Tamil' },
    { code: 'te', name: 'Telugu' },
    { code: 'kn', name: 'Kannada' },
    { code: 'bn', name: 'Bengali' },
    { code: 'mr', name: 'Marathi' },
    { code: 'ru', name: 'Russian' },
    { code: 'ar', name: 'Arabic' },
    { code: 'tr', name: 'Turkish' },
    { code: 'th', name: 'Thai' },
];

// Helper to get displayed languages based on user preferences
const getDisplayedLanguages = (userPreferredLanguages: string[]): { code: string; name: string }[] => {
    // Get languages that match user preferences
    const preferredLangs = ALL_AVAILABLE_LANGUAGES.filter(lang =>
        userPreferredLanguages.includes(lang.code)
    );

    // If user has less than 3 preferred languages, add random ones to make minimum 5 total
    if (preferredLangs.length < 3) {
        const nonPreferredLangs = ALL_AVAILABLE_LANGUAGES.filter(lang =>
            !userPreferredLanguages.includes(lang.code)
        );
        // Shuffle and take enough to make total 5
        const shuffled = nonPreferredLangs.sort(() => 0.5 - Math.random());
        const needed = 5 - preferredLangs.length;
        const additionalLangs = shuffled.slice(0, needed);
        return [...preferredLangs, ...additionalLangs];
    }

    return preferredLangs;
};

// ----------------------------------------------------------------------
// Animations
// ----------------------------------------------------------------------
const swipeAnimation = keyframes`
  0% { transform: translateX(0) rotate(0deg); opacity: 1; }
  15% { transform: translateX(100px) rotate(15deg); opacity: 0; }
  16% { transform: translateX(0) scale(0.9); opacity: 0; }
  25% { transform: translateX(0) scale(1); opacity: 1; }
  
  40% { transform: translateX(-100px) rotate(-15deg); opacity: 0; }
  41% { transform: translateX(0) scale(0.9); opacity: 0; }
  50% { transform: translateX(0) scale(1); opacity: 1; }
  
  65% { transform: translateY(100px) rotate(0deg); opacity: 0; }
  66% { transform: translateX(0) scale(0.9); opacity: 0; }
  75% { transform: translateX(0) scale(1); opacity: 1; }
  100% { transform: translateX(0) scale(1); opacity: 1; }
`;

const SlideUpTransition = React.forwardRef(function SlideUpTransition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------
const MovieTasteStepV2: React.FC<MovieTasteStepV2Props> = ({
    preferences,
    selectedLanguages,
    onPreferencesUpdate,
    onComplete,
    onBack,
    excludeMovieIds = new Set(),
    onMoviesSeen,
}) => {
    const theme = useTheme();

    // ✅ Track orientation changes for iPad support
    const { orientation, width, height } = useOrientationChange();

    // ✅ Add noSsr option to ensure proper re-evaluation on orientation changes
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

    // Compute displayed languages based on user preferences
    // If user has < 3 preferred languages, add random ones to make minimum 5 total
    const displayedLanguages = React.useMemo(() => {
        return getDisplayedLanguages(selectedLanguages || ['en']);
    }, [selectedLanguages]);

    // Data State
    const [step, setStep] = useState<'welcome' | 'swiping'>('welcome');
    const [movies, setMovies] = useState<TMDbMovie[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    // Initialize with ALL user preferred languages selected by default
    const [internalSelectedLanguages, setInternalSelectedLanguages] = useState<string[]>(
        () => {
            if (selectedLanguages && selectedLanguages.length > 0) {
                return selectedLanguages;
            }
            // Fallback: Default to English only
            return ['en'];
        }
    );

    // Interaction State
    const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
    const [dragDelta, setDragDelta] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [exitDirection, setExitDirection] = useState<'left' | 'right' | 'down' | null>(null);

    // Logic State
    const [viewedMovieIds, setViewedMovieIds] = useState<Set<string>>(new Set());
    const [history, setHistory] = useState<ActionHistoryItem[]>([]);
    const [undosUsed, setUndosUsed] = useState(0);

    // Rating Modal State
    const [showRatingPopup, setShowRatingPopup] = useState(false);
    const [ratingMovieId, setRatingMovieId] = useState<string | null>(null);
    const [localRating, setLocalRating] = useState<number | null>(null);

    // Completion State
    const [showCompletionPopup, setShowCompletionPopup] = useState(false);
    const [showActionLoader, setShowActionLoader] = useState(false);
    const [lastPopupCount, setLastPopupCount] = useState(0);

    // Toast State
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // Animation State
    const [isWantButtonAnimating, setIsWantButtonAnimating] = useState(false);

    // Dialog State
    const [dialogOpen, setDialogOpen] = useState(true);

    // Computed
    const markedMoviesCount = preferences.watchedMovies.length + preferences.watchlistMovies.length;
    const currentMovie = movies[currentIndex];
    const nextMovie = movies[currentIndex + 1];

    // ✅ Calculate optimal card dimensions based on orientation
    // This ensures cards fit properly when rotating between portrait and landscape
    const cardDimensions = useMemo(() => {
        return getOptimalCardDimensions(orientation, width, height);
    }, [orientation, width, height]);

    // Refs for cleanup
    const viewedMovieIdsRef = useRef(viewedMovieIds);
    const onMoviesSeenRef = useRef(onMoviesSeen);

    useEffect(() => {
        viewedMovieIdsRef.current = viewedMovieIds;
    }, [viewedMovieIds]);

    // Intro Animation State
    const [hasShownIntro, setHasShownIntro] = useState(false);
    const [isIntroAnimating, setIsIntroAnimating] = useState(false);

    useEffect(() => {
        onMoviesSeenRef.current = onMoviesSeen;
    }, [onMoviesSeen]);

    // Track viewed movies when currentIndex changes (similar to MovieTasteStep)
    useEffect(() => {
        if (currentMovie) {
            const movieId = getStandardizedMovieId(currentMovie);
            setViewedMovieIds(prev => {
                const newSet = new Set(prev);
                newSet.add(movieId);
                return newSet;
            });
        }
    }, [currentIndex, currentMovie]);

    // Intro Animation Logic
    useEffect(() => {
        if (step === 'swiping' && !loading && movies.length > 0 && !hasShownIntro) {
            setHasShownIntro(true);
            setIsIntroAnimating(true);
            // Sequence: Right -> Center -> Left -> Center -> Down -> Center
            const sequence = async () => {
                await new Promise(r => setTimeout(r, 1000));

                // Right (Watched)
                for (let i = 0; i <= 60; i += 2) { setDragDelta({ x: i, y: 0 }); await new Promise(r => setTimeout(r, 10)); }
                await new Promise(r => setTimeout(r, 400));
                for (let i = 60; i >= 0; i -= 2) { setDragDelta({ x: i, y: 0 }); await new Promise(r => setTimeout(r, 10)); }

                await new Promise(r => setTimeout(r, 200));

                // Left (Skip)
                for (let i = 0; i >= -60; i -= 2) { setDragDelta({ x: i, y: 0 }); await new Promise(r => setTimeout(r, 10)); }
                await new Promise(r => setTimeout(r, 400));
                for (let i = -60; i <= 0; i += 2) { setDragDelta({ x: i, y: 0 }); await new Promise(r => setTimeout(r, 10)); }

                await new Promise(r => setTimeout(r, 200));

                // Down (Want)
                for (let i = 0; i <= 60; i += 2) { setDragDelta({ x: 0, y: i }); await new Promise(r => setTimeout(r, 10)); }
                await new Promise(r => setTimeout(r, 400));
                for (let i = 60; i >= 0; i -= 2) { setDragDelta({ x: 0, y: i }); await new Promise(r => setTimeout(r, 10)); }

                setIsIntroAnimating(false);
            };
            sequence();
        }
    }, [step, loading, movies.length, hasShownIntro]);

    // Check if we should show completion popup
    useEffect(() => {
        const shouldShowPopup = (count: number) => {
            // Show after reaching minimum (6) or every 10 movies after that (16, 26, 36, etc.)
            if (count === 6) return true;
            if (count > 6 && (count - 6) % 10 === 0) return true;
            return false;
        };

        if (shouldShowPopup(markedMoviesCount) && markedMoviesCount > lastPopupCount) {
            setLastPopupCount(markedMoviesCount);
            // Small delay to let the user see their action completed first
            setTimeout(() => {
                setShowCompletionPopup(true);
            }, 800);
        }
    }, [markedMoviesCount, lastPopupCount]);

    // Unmount cleanup - defer to avoid blocking close animation
    useEffect(() => {
        return () => {
            const callback = onMoviesSeenRef.current;
            const viewedIds = viewedMovieIdsRef.current;
            if (callback && viewedIds.size > 0) {
                // Use setTimeout to defer the callback, allowing the dialog to close smoothly
                setTimeout(() => callback(Array.from(viewedIds)), 0);
            }
        };
    }, []);

    // ----------------------------------------------------------------------
    // Data Fetching
    // ----------------------------------------------------------------------
    const fetchMoreMovies = useCallback(async () => {
        if (isFetchingMore) return;
        setIsFetchingMore(true);
        try {
            const languages = internalSelectedLanguages.length > 0 ? internalSelectedLanguages : ['en'];
            const nextPage = currentPage + 1;

            console.log('🔄 MovieTasteStepV2: Fetching more movies for page:', nextPage);

            const dynamicMovies = await tmdbApi.getPopularAndTopRatedMoviesByLanguages(languages, 100, nextPage);
            // Filter out movies that have already been seen/marked OR viewed in this session
            const allExcluded = new Set([...Array.from(excludeMovieIds), ...Array.from(viewedMovieIds)]);
            const filteredMovies = dynamicMovies.filter(movie => {
                const mid = getStandardizedMovieId(movie);
                return !allExcluded.has(mid);
            });

            if (filteredMovies.length > 0) {
                console.log('✅ MovieTasteStepV2: Fetched', filteredMovies.length, 'new movies from page', nextPage);
                setMovies(prev => [...prev, ...filteredMovies]);
                setCurrentPage(nextPage);
            } else {
                console.warn('⚠️ MovieTasteStepV2: No new movies found on page', nextPage, '- all already seen');
            }
        } catch (error) {
            console.error('❌ MovieTasteStepV2: Error fetching more movies:', error);
        } finally {
            setIsFetchingMore(false);
        }
    }, [internalSelectedLanguages, excludeMovieIds, viewedMovieIds, currentPage, isFetchingMore]);

    const handleStart = async () => {
        setLoading(true);
        setStep('swiping');
        try {
            const languages = internalSelectedLanguages.length > 0 ? internalSelectedLanguages : ['en'];
            console.log('🎬 MovieTasteStepV2: Loading movies for languages:', languages);

            const dynamicMovies = await tmdbApi.getPopularAndTopRatedMoviesByLanguages(languages, 100, 1);
            const filteredMovies = dynamicMovies.filter(movie => {
                const mid = getStandardizedMovieId(movie);
                return !excludeMovieIds.has(mid);
            });

            // Debug: Log language distribution
            const languageDistribution = filteredMovies.reduce((acc: Record<string, number>, movie) => {
                acc[movie.original_language] = (acc[movie.original_language] || 0) + 1;
                return acc;
            }, {});
            console.log('🎬 MovieTasteStepV2: Loaded', filteredMovies.length, 'movies (filtered out', dynamicMovies.length - filteredMovies.length, 'already seen)');
            console.log('🌍 MovieTasteStepV2: Language distribution:', languageDistribution);

            setMovies(filteredMovies);
        } catch (err) {
            console.error('❌ MovieTasteStepV2: Error loading movies:', err);
        } finally {
            setLoading(false);
        }
    };

    const toggleLanguage = (code: string) => {
        setInternalSelectedLanguages(prev => {
            if (prev.includes(code)) return prev.filter(l => l !== code);
            return [...prev, code];
        });
    };

    // Prefetch when low
    useEffect(() => {
        if (movies.length > 0 && currentIndex >= movies.length - 8 && !isFetchingMore) {
            fetchMoreMovies();
        }
    }, [currentIndex, movies.length, isFetchingMore, fetchMoreMovies]);

    // ----------------------------------------------------------------------
    // Actions
    // ----------------------------------------------------------------------

    const triggerHaptic = (type: 'impact' | 'success' | 'warning') => {
        if (type === 'impact') void hapticLight();
        else if (type === 'success') void hapticSuccess();
        else if (type === 'warning') void hapticWarning();
    };

    const handleClose = () => {
        setDialogOpen(false);
        setTimeout(() => {
            if (onBack) onBack();
        }, 300);
    };

    const advanceCard = () => {
        setExitDirection(null);
        setDragDelta({ x: 0, y: 0 });
        setIsDragging(false); // Reset dragging state
        setCurrentIndex(prev => prev + 1);
        // viewedMovieIds is now tracked via useEffect on currentIndex change
        // Completion popup is now handled via useEffect on markedMoviesCount change
    };

    const handleDiscard = useCallback(() => {
        if (!currentMovie) return;
        setExitDirection('left');
        triggerHaptic('impact');

        const mid = getStandardizedMovieId(currentMovie);
        setHistory(prev => [...prev, { movieId: mid, action: 'discard' }]);

        setTimeout(() => {
            advanceCard();
        }, 400);
    }, [currentMovie]);

    const handleWatched = useCallback(() => {
        // Right Swipe -> Watched -> Open Mark As Watched Dialog
        if (!currentMovie) return;
        setExitDirection('right');
        triggerHaptic('success');

        // We delay standard processing until after dialog
        // But for visual feedback we animate card away first? 
        // Actually, usually you rate AFTER card leaves or AS it leaves.
        // Let's hide card, show dialog.

        // Prepare for rating - logic moved to onMarkAsWatched callback
        // Just trigger standard flow logic but don't commit to prefs yet?
        // Actually, simpler to just open dialog.

        setTimeout(() => {
            setShowRatingPopup(true); // Reusing this state name for MarkAsWatchedDialog
        }, 300);
    }, [currentMovie]);

    const handleMarkAsWatchedConfirm = (rating?: number, review?: string, isPrivate?: boolean, visibility?: 'anyone' | 'only-me', watchedDate?: Date | null) => {
        if (!currentMovie) return;
        const mid = getStandardizedMovieId(currentMovie);

        // Update Prefs
        const updatedWatchlist = preferences.watchlistMovies.filter(id => id !== mid);
        const updatedWatched = [...preferences.watchedMovies.filter(id => id !== mid), mid];

        const newRatings = { ...preferences.ratings };
        if (rating) newRatings[mid] = rating;

        const newMovieData = {
            title: currentMovie.title,
            poster: currentMovie.poster_path ? `https://image.tmdb.org/t/p/w500${currentMovie.poster_path}` : undefined,
            year: currentMovie.release_date ? new Date(currentMovie.release_date).getFullYear().toString() : undefined,
            rating: rating,
            review: review,
            reviewVisibility: visibility,
            watchedDate: watchedDate,
        };

        onPreferencesUpdate({
            ...preferences,
            watchlistMovies: updatedWatchlist,
            watchedMovies: updatedWatched,
            ratings: newRatings,
            movieDetails: {
                ...preferences.movieDetails,
                [mid]: newMovieData
            }
        });

        setHistory(prev => [...prev, { movieId: mid, action: 'watched' }]);
        setShowRatingPopup(false);

        // Show confirmation toast
        setToastMessage('Marked as watched');
        setToastOpen(true);

        advanceCard();
    };

    const handleWantToWatch = useCallback(() => {
        // Down Swipe -> Watchlist
        if (!currentMovie) return;
        setExitDirection('down');
        triggerHaptic('success');

        const mid = getStandardizedMovieId(currentMovie);

        // Update Prefs: Add to Watchlist, Remove from Watched
        const updatedWatched = preferences.watchedMovies.filter(id => id !== mid);
        const updatedWatchlist = [...preferences.watchlistMovies.filter(id => id !== mid), mid];

        onPreferencesUpdate({
            ...preferences,
            watchedMovies: updatedWatched,
            watchlistMovies: updatedWatchlist,
            movieDetails: {
                ...preferences.movieDetails,
                [mid]: {
                    title: currentMovie.title,
                    poster: currentMovie.poster_path ? `https://image.tmdb.org/t/p/w500${currentMovie.poster_path}` : undefined,
                    year: currentMovie.release_date ? new Date(currentMovie.release_date).getFullYear().toString() : undefined,
                }
            }
        });

        setHistory(prev => [...prev, { movieId: mid, action: 'want' }]);

        // Trigger button animation
        setIsWantButtonAnimating(true);
        setTimeout(() => setIsWantButtonAnimating(false), 300);

        // Show confirmation toast
        setToastMessage('Added to watchlist');
        setToastOpen(true);

        setTimeout(() => {
            advanceCard();
        }, 400);
    }, [currentMovie, preferences, onPreferencesUpdate]);

    const handleUndo = useCallback(() => {
        if (history.length === 0 || undosUsed >= MAX_UNDOS || currentIndex === 0) return;

        const lastAction = history[history.length - 1];
        setUndosUsed(prev => prev + 1);
        setHistory(prev => [...prev.slice(0, -1)]); // Correctly remove last item
        setCurrentIndex(prev => prev - 1);
        setExitDirection(null); // Reset any exit animation state for the returning card

        triggerHaptic('impact');

        // Revert Preferences Logic
        const mid = lastAction.movieId;
        if (lastAction.action === 'want') {
            // It was added to watchlist, so remove it
            const updatedWatchlist = preferences.watchlistMovies.filter(id => id !== mid);
            onPreferencesUpdate({ ...preferences, watchlistMovies: updatedWatchlist });
        } else if (lastAction.action === 'watched') {
            // It was added to watched, so remove it. Also possibly remove rating.
            const updatedWatched = preferences.watchedMovies.filter(id => id !== mid);
            const { [mid]: removed, ...restRatings } = preferences.ratings;
            onPreferencesUpdate({ ...preferences, watchedMovies: updatedWatched, ratings: restRatings });
        }
        // If discard, no prefs change needed
    }, [history, undosUsed, currentIndex, preferences, onPreferencesUpdate]);

    // ----------------------------------------------------------------------
    // Gesture Handling
    // ----------------------------------------------------------------------
    const handleTouchStart = (e: React.TouchEvent) => {
        if (showRatingPopup || showCompletionPopup) return;
        setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
        setIsDragging(true);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!dragStart || !isDragging) return;
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const dx = currentX - dragStart.x;
        const dy = currentY - dragStart.y;
        setDragDelta({ x: dx, y: dy });
    };

    const handleTouchEnd = () => {
        if (!isDragging) return;

        // Determine action based on threshold
        const absX = Math.abs(dragDelta.x);
        const absY = Math.abs(dragDelta.y);

        if (dragDelta.y > SWIPE_THRESHOLD && absY > absX) {
            // Down Swipe (Want)
            handleWantToWatch();
        } else if (dragDelta.x > SWIPE_THRESHOLD && absX > absY) {
            // Right Swipe (Watched)
            handleWatched();
        } else if (dragDelta.x < -SWIPE_THRESHOLD && absX > absY) {
            // Left Swipe (Discard)
            handleDiscard();
        } else {
            // Reset
            setDragDelta({ x: 0, y: 0 });
        }

        setIsDragging(false);
        setDragStart(null);
    };

    // Rating Logic
    const handleRatingSelect = (rating: number) => {
        if (!ratingMovieId) return;

        // Add rating to preferences
        onPreferencesUpdate({
            ...preferences,
            ratings: { ...preferences.ratings, [ratingMovieId]: rating }
        });

        setLocalRating(null);
        setShowRatingPopup(false);
        setRatingMovieId(null);
        setRatingMovieId(null);
        triggerHaptic('success');
    };

    const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setToastOpen(false);
    };

    // ----------------------------------------------------------------------
    // Render Helpers
    // ----------------------------------------------------------------------


    // Styles for the active card based on drag
    const rotateDeg = dragDelta.x * 0.05;
    const activeCardStyle = {
        transform: exitDirection
            ? exitDirection === 'left' ? `translate(-150%, 0) rotate(-20deg)`
                : exitDirection === 'right' ? `translate(150%, 0) rotate(20deg)`
                    : `translate(0, 150%) rotate(0deg)`
            : `translate(${dragDelta.x}px, ${dragDelta.y}px) rotate(${rotateDeg}deg)`,
        transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        zIndex: 10,
        touchAction: 'none', // Critical for preventing scroll interference
    };

    // Overlay opacities
    // Overlay opacities
    const nopeOpacity = Math.max(0, Math.min(1, -dragDelta.x / (SWIPE_THRESHOLD * 0.8)));
    const seenOpacity = Math.max(0, Math.min(1, dragDelta.x / (SWIPE_THRESHOLD * 0.8)));
    const wantOpacity = Math.max(0, Math.min(1, dragDelta.y / (SWIPE_THRESHOLD * 0.8)));

    // ----------------------------------------------------------------------
    // JSX
    // ----------------------------------------------------------------------

    // Welcome screen content
    const welcomeContent = (
        <>
            {/* Header - with safe area support for fullScreen mode */}
            <Box sx={{
                px: 3,
                // Include safe-area-inset-top for iOS PWA status bar in fullScreen mode
                pt: isMobile ? 'calc(var(--safe-area-inset-top) + 16px)' : 2,
                pb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                position: 'relative',
                zIndex: 10
            }}>
                <Box sx={{ width: 40 }} /> {/* Spacer */}

                <Typography sx={{
                    fontFamily: font(),
                    fontWeight: 700,
                    fontSize: 18,
                    textAlign: 'center',
                    color: 'white',
                    opacity: 0 // Hide title on start screen
                }}>
                    Quick Takes
                </Typography>

                <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                gap: 4,
                overflowY: 'auto',
                zIndex: 10
            }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h4" sx={{
                        fontFamily: font(),
                        fontWeight: 800,
                        mb: 2,
                        background: 'linear-gradient(45deg, #bbb, #fff)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'transparent'
                    }}>
                        Quick Takes
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 400 }}>
                        Swipe through films — add what you've watched or want to see.
                    </Typography>
                </Box>

                <Box sx={{
                    width: '100%',
                    maxWidth: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    p: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 4,
                    border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            justifyContent: 'center'
                        }}>
                            {displayedLanguages.map((lang) => {
                                const isSelected = internalSelectedLanguages.includes(lang.code);
                                return (
                                    <Box
                                        key={lang.code}
                                        onClick={() => toggleLanguage(lang.code)}
                                        sx={{
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 20,
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            border: `1px solid ${isSelected ? '#fff' : 'rgba(255,255,255,0.2)'}`,
                                            bgcolor: isSelected ? '#fff' : 'transparent',
                                            color: isSelected ? '#000' : 'rgba(255,255,255,0.8)',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            '&:hover': {
                                                bgcolor: isSelected ? '#fff' : 'rgba(255,255,255,0.05)'
                                            }
                                        }}
                                    >
                                        {isSelected && <Check sx={{ fontSize: 16 }} />}
                                        {lang.name}
                                    </Box>
                                );

                            })}


                        </Box>
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mt: -2, mb: 2 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 400, fontSize: 14 }}>
                        {internalSelectedLanguages.length} language{internalSelectedLanguages.length !== 1 ? 's' : ''} selected
                    </Typography>
                </Box>

                <CustomButton
                    variant="contained"
                    semantic="brand"
                    size="medium"
                    onClick={handleStart}
                    label="Start Swiping"
                    icon={<PlayArrow />} // Ensure PlayArrow is imported or swap for visible icon
                    disabled={internalSelectedLanguages.length === 0}
                    sx={{ mt: 2, minWidth: 200, py: 1.5 }}
                />
            </Box>
        </>
    );


    const swipingContent = (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                bgcolor: '#000',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Header - with safe area support */}
            <Box sx={{
                height: isMobile ? 'calc(var(--safe-area-inset-top) + 60px)' : 60,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2, pt: isMobile ? 'var(--safe-area-inset-top)' : 0, zIndex: 50,
                position: 'relative'
            }}>
                {/* Undo Button (Left of Header) */}
                <Box sx={{ width: 80, display: 'flex', alignItems: 'center' }}>
                    {!loading && undosUsed < MAX_UNDOS && history.length > 0 && currentIndex > 0 && (
                        <CustomButton
                            onClick={handleUndo}
                            icon={<Replay sx={{ fontSize: 20 }} />}
                            variant="text"
                            sx={{
                                color: 'rgba(255,255,255,0.6)',
                                textTransform: 'none',
                                fontSize: 14,
                                px: 1.5,
                                py: 0.5,
                                bgcolor: 'rgba(0,0,0,0.2)',
                                backdropFilter: 'blur(4px)',
                                borderRadius: 2
                            }}
                        >
                            Undo
                        </CustomButton>
                    )}
                </Box>

                <Typography sx={{
                    fontFamily: font(),
                    fontWeight: 700,
                    fontSize: 16,
                    color: 'white',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    pointerEvents: 'none',
                    zIndex: -1
                }}>
                    Quick Takes
                </Typography>

                <Box sx={{ width: 80, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.8)', bgcolor: 'rgba(0,0,0,0.2)' }}>
                        <Close />
                    </IconButton>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    flex: 1, gap: 4
                }} >
                    <Box sx={{
                        position: 'relative', width: 120, height: 180,
                        perspective: 1000
                    }}>
                        {/* Static back card */}
                        <Box sx={{
                            position: 'absolute', inset: 0,
                            bgcolor: 'rgba(255,255,255,0.05)',
                            borderRadius: 4,
                            border: '1px solid rgba(255,255,255,0.1)',
                            transform: 'scale(0.9) translateY(15px)',
                            zIndex: 1
                        }} />
                        {/* Animated front card */}
                        <Box sx={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.02) 100%)',
                            borderRadius: 4,
                            border: '1px solid rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            animation: `${swipeAnimation} 5s infinite ease-in-out`,
                            zIndex: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            backdropFilter: 'blur(10px)'
                        }}>
                            <PlayArrow sx={{ opacity: 0.3, fontSize: 40 }} />
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 4 }}>
                        <Typography sx={{ color: 'white', fontFamily: font(), fontWeight: 700, fontSize: 20, mb: 1 }}>
                            Quick Takes
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: font(), fontSize: 15, maxWidth: 240, mx: 'auto', lineHeight: 1.4 }}>
                            Swipe through films to add to your watchlist or mark as watched
                        </Typography>
                    </Box>
                </Box >
            ) : (
                <>
                    {/* Dynamic Blurred Background */}
                    {
                        currentMovie && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 0,
                                    opacity: 0.6,
                                    backgroundImage: `url(https://image.tmdb.org/t/p/w500${currentMovie.poster_path})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    filter: 'blur(30px) brightness(0.5)',
                                    transition: 'background-image 0.5s ease-in-out',
                                }}
                            />
                        )
                    }

                    {/* Gradient Overlay for better text visibility */}
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 1,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.95) 100%)',
                        }}
                    />

                    {/* Dynamic Full Screen Overlays */}
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        pointerEvents: 'none', zIndex: 20, // Sit above cards to ensure visibility, below action buttons
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: isIntroAnimating ? 'none' : 'opacity 0.2s ease',
                    }}>
                        {/* Skip Overlay (Left Swipe) */}
                        <Box sx={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to right, rgba(255, 69, 58, 0.6) 0%, rgba(255, 69, 58, 0.2) 50%, transparent 100%)',
                            opacity: nopeOpacity,
                            display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                            pl: '15%'
                        }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', transform: 'rotate(-10deg)', opacity: Math.min(1, nopeOpacity * 1.5) }}>
                                <Close sx={{ fontSize: 72, color: '#FF453A', mb: 1, filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.8))' }} />
                                <Typography sx={{ color: '#FF453A', fontWeight: 900, fontSize: 36, textShadow: '0px 4px 12px rgba(0,0,0,0.9)' }}>
                                    Skipping
                                </Typography>
                            </Box>
                        </Box>

                        {/* Watched Overlay (Right Swipe) */}
                        <Box sx={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to left, rgba(48, 219, 91, 0.6) 0%, rgba(48, 219, 91, 0.2) 50%, transparent 100%)',
                            opacity: seenOpacity,
                            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                            pr: '15%'
                        }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', transform: 'rotate(10deg)', opacity: Math.min(1, seenOpacity * 1.5) }}>
                                <Check sx={{ fontSize: 72, color: '#30DB5B', mb: 1, filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.8))' }} />
                                <Typography sx={{ color: '#30DB5B', fontWeight: 900, fontSize: 36, textShadow: '0px 4px 12px rgba(0,0,0,0.9)' }}>
                                    Watched
                                </Typography>
                            </Box>
                        </Box>

                        {/* Want to Watch Overlay (Down Swipe) */}
                        <Box sx={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to top, rgba(60, 58, 159, 0.7) 0%, rgba(125, 122, 255, 0.2) 50%, transparent 100%)',
                            opacity: wantOpacity,
                            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                            pb: '45%' // Raised significantly so it is not hidden under the action bar
                        }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: Math.min(1, wantOpacity * 1.5) }}>
                                <PlaylistAdd sx={{ fontSize: 72, color: '#7D7AFF', mb: 1, filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.8))' }} />
                                <Typography sx={{ color: '#7D7AFF', fontWeight: 900, fontSize: 36, textShadow: '0px 4px 12px rgba(0,0,0,0.9)' }}>
                                    Want to Watch
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Card Deck Area */}
                    <Box sx={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

                        {/* Next Card (Background) */}
                        {nextMovie && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    height: `${cardDimensions.heightPercent * 100}%`, // ✅ Dynamic based on orientation
                                    aspectRatio: '2/3',
                                    maxWidth: `${cardDimensions.widthPercent * 100}%`, // ✅ Dynamic based on orientation
                                    borderRadius: '20px',
                                    overflow: 'hidden',
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                                    zIndex: 5,
                                }}
                            >
                                <img
                                    src={nextMovie.poster_path ? `https://image.tmdb.org/t/p/w500${nextMovie.poster_path}` : ''}
                                    alt={nextMovie.title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.6)' }}
                                />
                            </Box>
                        )}

                        {/* Active Card */}
                        {currentMovie ? (
                            <Box
                                key={currentMovie.id} // Force new element on movie change
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                sx={{
                                    position: 'absolute',
                                    height: `${cardDimensions.heightPercent * 100}%`, // ✅ Dynamic based on orientation
                                    aspectRatio: '2/3',
                                    maxWidth: `${cardDimensions.widthPercent * 100}%`, // ✅ Dynamic based on orientation
                                    borderRadius: '20px',
                                    overflow: 'hidden',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                                    bgcolor: 'black',
                                    cursor: 'grab',
                                    ...activeCardStyle
                                }}
                            >
                                <img
                                    src={currentMovie.poster_path ? `https://image.tmdb.org/t/p/w500${currentMovie.poster_path}` : ''}
                                    alt={currentMovie.title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', userSelect: 'none', pointerEvents: 'none' }}
                                    draggable={false}
                                />

                                {/* Info Gradient - Simpler without Box to avoid cropping */}
                                <Box sx={{
                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                    background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
                                    p: 3, pt: 8, color: 'white', pointerEvents: 'none'
                                }}>
                                    <Typography variant="h5" fontWeight="700">{currentMovie.title}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                        {currentMovie.release_date?.split('-')[0]} • {currentMovie.genres?.[0]?.name}
                                    </Typography>
                                </Box>

                                {/* Overlays removed: handeled by the Dynamic Full Screen Overlays above */}
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', color: 'white' }}>
                                <Typography variant="h6">No more movies!</Typography>
                                <CustomButton onClick={onComplete} variant="contained" semantic="brand" sx={{ mt: 2 }}>
                                    Finish Setup
                                </CustomButton>
                            </Box>
                        )}
                    </Box>

                    {/* Action Bar */}
                    <Box sx={{
                        height: 140, width: '100%', maxWidth: 400, mx: 'auto',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-evenly',
                        pb: 2, zIndex: 50
                    }}>
                        {/* Discard (Left) */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                onClick={handleDiscard}
                                sx={{
                                    width: 60, height: 60, bgcolor: 'rgba(255, 69, 58, 0.1)', color: '#FF453A',
                                    border: '2px solid rgba(255, 69, 58, 0.3)',
                                    '&:hover': { bgcolor: 'rgba(255, 69, 58, 0.2)' }
                                }}>
                                <CaretLeftIcon size={40} />
                            </IconButton>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Skip</Typography>
                        </Box>

                        {/* Want (Down) - Purple */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 2 }}>
                            <IconButton
                                onClick={handleWantToWatch}
                                sx={{
                                    width: 50, height: 50, color: '#8E8AFF',
                                    border: '2px solid rgba(125, 122, 255, 0.3)',
                                    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                    transform: isWantButtonAnimating ? 'scale(1.2)' : 'scale(1)',
                                    bgcolor: isWantButtonAnimating ? 'rgba(125, 122, 255, 0.3)' : 'rgba(125, 122, 255, 0.1)',
                                    '&:hover': { bgcolor: 'rgba(125, 122, 255, 0.2)' }
                                }}>
                                <CaretDownIcon size={32} />
                            </IconButton>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Want to Watch</Typography>
                        </Box>

                        {/* Watched (Right) - Green - Prominent */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                onClick={handleWatched}
                                sx={{
                                    width: 70, height: 70, bgcolor: '#30DB5B', color: 'white',
                                    boxShadow: '0 8px 20px rgba(48, 219, 91, 0.4)',
                                    '&:hover': { bgcolor: '#2BC653', transform: 'scale(1.05)' }
                                }}>
                                <CaretRightIcon size={48} />
                            </IconButton>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Watched</Typography>
                        </Box>
                    </Box>



                    {/* Mark As Watched Dialog */}
                    {currentMovie && showRatingPopup && (
                        <MarkAsWatchedDialog
                            open={showRatingPopup}
                            onClose={() => {
                                setShowRatingPopup(false);
                                // Don't show toast on close - it's called both on cancel AND submit
                                setExitDirection(null);
                                setDragDelta({ x: 0, y: 0 });
                            }}
                            movie={currentMovie}
                            onMarkAsWatched={handleMarkAsWatchedConfirm}
                            zIndex={1300}
                        />
                    )}

                    {/* Completion Modal */}
                    <Dialog
                        open={showCompletionPopup}
                        onClose={() => setShowCompletionPopup(false)}
                        PaperProps={{
                            sx: {
                                borderRadius: 4,
                                bgcolor: '#1a1a2e',
                                backgroundImage: 'none',
                                maxWidth: 320,
                                mx: 2,
                            }
                        }}
                        BackdropProps={{
                            sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
                        }}
                    >
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontFamily: font(),
                                    mb: 1
                                }}
                            >
                                Congrats!
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.7)',
                                    fontFamily: font(),
                                    mb: 4
                                }}
                            >
                                You have added {markedMoviesCount} movies to your list
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <CustomButton
                                    variant="contained"
                                    semantic="brand"
                                    onClick={() => setShowCompletionPopup(false)}
                                    sx={{ width: '100%' }}
                                >
                                    Swipe More
                                </CustomButton>
                                <CustomButton
                                    variant="outlined"
                                    onClick={onComplete}
                                    sx={{
                                        width: '100%',
                                        color: 'rgba(255,255,255,0.8)',
                                        borderColor: 'rgba(255,255,255,0.3)',
                                        '&:hover': {
                                            borderColor: 'rgba(255,255,255,0.5)',
                                            bgcolor: 'rgba(255,255,255,0.05)'
                                        }
                                    }}
                                >
                                    Stop
                                </CustomButton>
                            </Box>
                        </Box>
                    </Dialog>

                    {showActionLoader && <ActionLoader />}

                    <Snackbar
                        open={toastOpen}
                        autoHideDuration={2000}
                        onClose={handleToastClose}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        sx={{ top: { xs: 60, sm: 80 } }} // Position below header
                    >
                        <Alert onClose={handleToastClose} severity="success" sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.9)', color: '#101010ac', fontWeight: 600 }}>
                            {toastMessage}
                        </Alert>
                    </Snackbar>
                </>
            )}
        </Box >
    );

    return (
        <Dialog
            fullScreen={isMobile}
            open={dialogOpen}
            onClose={handleClose}
            TransitionComponent={isMobile ? SlideUpTransition : undefined}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 4,
                    maxWidth: isMobile ? '100%' : '800px',
                    width: isMobile ? '100%' : '800px',
                    height: isMobile ? '100%' : '80vh',
                    overflow: 'hidden',
                    bgcolor: '#000',
                    backgroundImage: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                }
            }}
            BackdropProps={{
                sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
            }}
        >
            {step === 'welcome' ? welcomeContent : swipingContent}
        </Dialog>
    );
};

export default MovieTasteStepV2;
